import React, { useMemo } from "react";
import T from '../../components/common/T';
import { Search } from "lucide-react";
import { UserGroupIcon, CheckCircleIcon, ClockIcon, XCircleIcon } from "@heroicons/react/24/outline";
import DataTable from '../../components/common/DataTables';
import { GeofenceBadge } from "./AttendenceShared";
import { StatsGrid } from "../../components/common/PageLayout";

export default function AttendanceListView({
  isAdmin, listData, filteredListData,
  searchTerm, setSearchTerm, statusFilter, setStatusFilter,
  dateFilter, setDateFilter, monthFilter, setMonthFilter
}) {
  const getStatusStyles = (status) => {
    const s = (status || "").toLowerCase();
    if (["present","on time"].includes(s))             return "bg-green-50  text-green-700  border-green-200";
    if (s === "late")                                  return "bg-orange-50 text-orange-700 border-orange-200";
    if (["half day","half_day"].includes(s))           return "bg-blue-50   text-blue-700   border-blue-200";
    if (["absent","on leave","on_leave"].includes(s))  return "bg-red-50    text-red-700    border-red-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  const stats = useMemo(() => [
    {
      label: "Total Staff",
      value: listData.length,
      icon: UserGroupIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => setStatusFilter("All"),
    },
    {
      label: "Present",
      value: listData.filter(r => ["present","on time"].includes((r.status||"").toLowerCase())).length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: statusFilter === "Present",
      onClick: () => setStatusFilter(s => s === "Present" ? "All" : "Present"),
    },
    {
      label: "Late",
      value: listData.filter(r => (r.status||"").toLowerCase() === "late").length,
      icon: ClockIcon,
      variant: "orange",
      isActive: statusFilter === "Late",
      onClick: () => setStatusFilter(s => s === "Late" ? "All" : "Late"),
    },
    {
      label: "Absent",
      value: listData.filter(r => ["absent","on leave","half day","half_day"].includes((r.status||"").toLowerCase().replace("_", " "))).length,
      icon: XCircleIcon,
      variant: "red",
      isActive: statusFilter === "Absent",
      onClick: () => setStatusFilter(s => s === "Absent" ? "All" : "Absent"),
    },
  ], [listData, statusFilter]);

  const listColumns = useMemo(() => {
    const cols = [];
    if (isAdmin) {
      cols.push({
        header: "Employee",
        accessor: "name",
        render: (row) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
              {row.avatar}
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">{row.name || "Unknown"}</p>
              <p className="text-xs text-gray-500">{row.role || "—"}</p>
            </div>
          </div>
        )
      });
    } else {
      cols.push({
        header: "Date",
        accessor: "attendance_date",
        render: (row) => (
          <span className="font-medium text-gray-700 text-sm">
            {new Date(row.attendance_date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
          </span>
        )
      });
    }
    cols.push({ header: "Check-In", accessor: "checkIn", cellClassName: "font-medium text-sm", render: (row) => row.checkIn || "—" });
    cols.push({ header: "Check-Out", accessor: "checkOut", cellClassName: "text-gray-500 text-sm", render: (row) => row.checkOut || "—" });
    cols.push({ header: "Hours", accessor: "hours", cellClassName: "font-mono text-gray-600 text-sm", render: (row) => row.hours || "—" });
    cols.push({
      header: "Status",
      accessor: "status",
      render: (row) => (
        <span className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-xs font-bold uppercase border ${getStatusStyles(row.status)}`}>
          {row.status}
        </span>
      )
    });
    cols.push({
      header: "Geofence",
      accessor: "geofence_status",
      render: (row) => <GeofenceBadge status={row.geofence_status} />
    });
    return cols;
  }, [isAdmin]);

  return (
    <div className="animate-in fade-in duration-200 flex-1 flex flex-col">
      {isAdmin && (
        <div className="mb-4 sm:mb-5 shrink-0">
          <StatsGrid stats={stats} cols={4} />
        </div>
      )}

      <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm mb-4 sm:mb-5 flex flex-col sm:flex-row gap-3 shrink-0">
        {isAdmin && (
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Search employee…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        )}
        {isAdmin ? (
          <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500" />
        ) : (
          <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500" />
        )}
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500">
          <option value="All">All Statuses</option>
          <option value="Present">Present</option>
          <option value="Late">Late</option>
          <option value="Half Day">Half Day</option>
          <option value="Absent">Absent</option>
          <option value="On Leave">On Leave</option>
        </select>
      </div>

      <DataTable 
        data={filteredListData} 
        columns={listColumns} 
        keyExtractor={(item, index) => item.dbId || item.id || index}
        emptyMessage={isAdmin ? "No attendance records found for this date." : "No attendance records found for this month."}
      />
    </div>
  );
}